import type { BuiltInFieldErrorCode, FieldErrorCode, FormerieErrorCode } from "./types.js";

export const DEFAULT_ERROR_MESSAGES: Record<FormerieErrorCode, string> = {
  BAD_REQUEST: "The request could not be processed.",
  INVALID_JSON: "The request body must be valid JSON.",
  FORM_NOT_FOUND: "The requested form was not found.",
  FORM_DISABLED: "The requested form is disabled.",
  FORBIDDEN_ORIGIN: "This origin is not allowed for this form.",
  VALIDATION_FAILED: "The submitted form contains invalid fields.",
  SPAM_REJECTED: "The submission was rejected.",
  TURNSTILE_FAILED: "Anti-spam verification failed.",
  QUEUE_FAILED: "The submission could not be queued.",
  UNAUTHORIZED: "Authentication is required.",
  DEAD_LETTER_NOT_FOUND: "The requested dead-letter job was not found.",
  DEAD_LETTER_NOT_REPLAYABLE: "The requested dead-letter job cannot be replayed.",
  RATE_LIMITED: "Too many submissions. Please try again later.",
  FORM_QUOTA_REACHED: "This form is no longer accepting submissions.",
  FORM_NOT_OPEN: "This form is not accepting submissions yet.",
  FORM_CLOSED: "This form is no longer accepting submissions.",
  DUPLICATE_SUBMISSION: "This submission was already accepted.",
  INTERNAL_ERROR: "An internal error occurred.",
  NETWORK_ERROR: "The Formerie API could not be reached.",
  INVALID_RESPONSE: "The Formerie API returned an invalid response.",
};

export const DEFAULT_FIELD_ERROR_MESSAGES: Record<BuiltInFieldErrorCode, string> = {
  FIELD_REQUIRED: "This field is required.",
  FIELD_TYPE_INVALID: "This field has an invalid type.",
  FIELD_STRING_TOO_SHORT: "This field is too short.",
  FIELD_STRING_TOO_LONG: "This field is too long.",
  FIELD_STRING_LENGTH_INVALID: "This field has an invalid length.",
  FIELD_STRING_PREFIX_INVALID: "This field has an invalid prefix.",
  FIELD_STRING_SUFFIX_INVALID: "This field has an invalid suffix.",
  FIELD_STRING_CONTAINS_REQUIRED: "This field is missing required text.",
  FIELD_STRING_CONTAINS_FORBIDDEN: "This field contains unsupported text.",
  FIELD_EMAIL_INVALID: "Invalid email address.",
  FIELD_PHONE_INVALID: "Invalid phone number.",
  FIELD_UUID_INVALID: "Invalid UUID.",
  FIELD_COUNTRY_CODE_INVALID: "Invalid country code.",
  FIELD_POSTAL_CODE_INVALID: "Invalid postal code.",
  FIELD_REGION_CODE_INVALID: "Invalid region code.",
  FIELD_URL_INVALID: "Invalid URL.",
  FIELD_URL_PROTOCOL_NOT_ALLOWED: "This URL protocol is not allowed.",
  FIELD_DATE_INVALID: "Invalid date.",
  FIELD_DATE_TOO_EARLY: "This date is too early.",
  FIELD_DATE_TOO_LATE: "This date is too late.",
  FIELD_TIME_INVALID: "Invalid time.",
  FIELD_DATETIME_INVALID: "Invalid date-time.",
  FIELD_NUMBER_TOO_SMALL: "This number is too small.",
  FIELD_NUMBER_TOO_LARGE: "This number is too large.",
  FIELD_INTEGER_REQUIRED: "This number must be an integer.",
  FIELD_DECIMAL_PLACES_INVALID: "This number has too many decimal places.",
  FIELD_VALUE_NOT_ALLOWED: "This field contains an unsupported value.",
  FIELD_PATTERN_INVALID: "This field has an invalid format.",
  FIELD_ACCEPTED_REQUIRED: "This field must be accepted.",
  FIELD_SELECTION_TOO_FEW: "Not enough options are selected.",
  FIELD_SELECTION_TOO_MANY: "Too many options are selected.",
  FIELD_ARRAY_TOO_SHORT: "This field does not include enough items.",
  FIELD_ARRAY_TOO_LONG: "This field includes too many items.",
  FIELD_ARRAY_DUPLICATES: "This field contains duplicate items.",
  FIELD_OBJECT_INVALID: "This field must be an object.",
  FORM_RULE_FAILED: "The form contains an invalid combination of values.",
};

export function messageForErrorCode(code: FormerieErrorCode): string {
  return DEFAULT_ERROR_MESSAGES[code] ?? DEFAULT_ERROR_MESSAGES.INTERNAL_ERROR;
}

export function messageForFieldErrorCode(code: FieldErrorCode): string {
  return (
    DEFAULT_FIELD_ERROR_MESSAGES[code as BuiltInFieldErrorCode] ??
    DEFAULT_FIELD_ERROR_MESSAGES.FIELD_TYPE_INVALID
  );
}
